import type {
  DailyCapacity,
  EnrichmentActionT,
  EnrichmentChange,
  EnrichmentEvent,
  EvidenceSourceT,
  FieldEvidence,
  PrioritizationEntityTypeT,
  ProgressEvent,
  ProtectedBlock,
  TriageDisposition,
  ValueStateT,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import {
  type WithNow,
  getDailyCapacitiesMap,
  getEnrichmentEventsMap,
  getFieldEvidenceMap,
  getInitiativesMap,
  getObjectivesMap,
  getProgressEventsMap,
  getProtectedBlocksMap,
  getTriageDispositionsMap,
} from './doc.ts';

export interface SetDailyCapacityInput {
  localDate: string;
  timezone: string;
  availableMinutes: number;
}

export interface CreateProtectedBlockInput {
  localDate: string;
  timezone: string;
  durationMinutes: number;
  startAt?: number;
  endAt?: number;
  objectiveId?: string;
  initiativeId?: string;
  note?: string;
}

export type UpdateProtectedBlockInput = Partial<CreateProtectedBlockInput>;

export interface RecordFieldEvidenceInput {
  entityType: PrioritizationEntityTypeT;
  entityId: string;
  field: string;
  valueState: ValueStateT;
  source: EvidenceSourceT;
  actor: string;
  tool?: string;
  valueHash?: string;
  confirmationId?: string;
  proposalFingerprint?: string;
}

export interface RecordProgressEventInput {
  entityType: 'objective' | 'initiative';
  entityId: string;
  occurredAt?: number;
  note?: string;
  source: EvidenceSourceT;
  actor: string;
  tool?: string;
}

export interface RecordEnrichmentEventInput {
  entityType: PrioritizationEntityTypeT;
  entityId: string;
  action: EnrichmentActionT;
  changes?: EnrichmentChange[];
  actor: string;
  tool?: string;
  confirmationId?: string;
  proposalFingerprint?: string;
}

export interface SetTriageDispositionInput {
  entityType: PrioritizationEntityTypeT;
  entityId: string;
  disposition: 'skipped' | 'rejected' | 'deferred';
  reasonCodes: string[];
  sourceFingerprint: string;
  resurfaceAt?: number;
  actor: string;
  tool?: string;
}

function timestamp(options: WithNow): number {
  return options.now ?? Date.now();
}

function capacityId(localDate: string, timezone: string): string {
  return `capacity:${timezone}:${localDate}`;
}

function fieldEvidenceId(entityType: string, entityId: string, field: string): string {
  return `evidence:${entityType}:${entityId}:${field}`;
}

function dispositionId(entityType: string, entityId: string): string {
  return `triage:${entityType}:${entityId}`;
}

export function getDailyCapacity(
  doc: Y.Doc,
  localDate: string,
  timezone: string,
): DailyCapacity | undefined {
  const id = capacityId(localDate, timezone);
  const inner = getDailyCapacitiesMap(doc).get(id);
  return inner ? dailyCapacityFromMap(id, inner) : undefined;
}

export function listDailyCapacities(doc: Y.Doc): DailyCapacity[] {
  return [...getDailyCapacitiesMap(doc).entries()]
    .map(([id, inner]) => dailyCapacityFromMap(id, inner))
    .sort((a, b) => a.localDate.localeCompare(b.localDate) || a.timezone.localeCompare(b.timezone));
}

export function setDailyCapacity(
  doc: Y.Doc,
  input: SetDailyCapacityInput,
  options: WithNow = {},
): DailyCapacity {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.localDate)) throw new Error('invalid localDate');
  if (!input.timezone.trim()) throw new Error('timezone is required');
  if (!Number.isInteger(input.availableMinutes) || input.availableMinutes < 0) {
    throw new Error('availableMinutes must be a non-negative integer');
  }
  const now = timestamp(options);
  const id = capacityId(input.localDate, input.timezone);
  const map = getDailyCapacitiesMap(doc);
  const existing = map.get(id);
  const inner = existing ?? new Y.Map<unknown>();
  doc.transact(() => {
    if (!existing) map.set(id, inner);
    inner.set('localDate', input.localDate);
    inner.set('timezone', input.timezone);
    inner.set('availableMinutes', input.availableMinutes);
    if (!inner.has('createdAt')) inner.set('createdAt', now);
    inner.set('updatedAt', now);
  });
  return dailyCapacityFromMap(id, inner);
}

export function listProtectedBlocks(doc: Y.Doc): ProtectedBlock[] {
  return [...getProtectedBlocksMap(doc).entries()]
    .map(([id, inner]) => protectedBlockFromMap(id, inner))
    .sort((a, b) => a.localDate.localeCompare(b.localDate) || (a.startAt ?? 0) - (b.startAt ?? 0));
}

export function getProtectedBlock(doc: Y.Doc, id: string): ProtectedBlock | undefined {
  const inner = getProtectedBlocksMap(doc).get(id);
  return inner ? protectedBlockFromMap(id, inner) : undefined;
}

export function createProtectedBlock(
  doc: Y.Doc,
  input: CreateProtectedBlockInput,
  options: WithNow = {},
): ProtectedBlock {
  validateProtectedBlock(doc, input);
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  setProtectedBlockFields(inner, input);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  getProtectedBlocksMap(doc).set(id, inner);
  return protectedBlockFromMap(id, inner);
}

export function updateProtectedBlock(
  doc: Y.Doc,
  id: string,
  patch: UpdateProtectedBlockInput,
  options: WithNow = {},
): void {
  const inner = getProtectedBlocksMap(doc).get(id);
  if (!inner) return;
  const current = protectedBlockFromMap(id, inner);
  const candidate = { ...current, ...patch };
  validateProtectedBlock(doc, candidate);
  const now = timestamp(options);
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) setOrDelete(inner, key, value);
    inner.set('updatedAt', now);
  });
}

export function deleteProtectedBlock(doc: Y.Doc, id: string): void {
  getProtectedBlocksMap(doc).delete(id);
}

export function getFieldEvidence(
  doc: Y.Doc,
  entityType: PrioritizationEntityTypeT,
  entityId: string,
  field: string,
): FieldEvidence | undefined {
  const id = fieldEvidenceId(entityType, entityId, field);
  const inner = getFieldEvidenceMap(doc).get(id);
  return inner ? fieldEvidenceFromMap(id, inner) : undefined;
}

export function listFieldEvidence(doc: Y.Doc): FieldEvidence[] {
  return [...getFieldEvidenceMap(doc).entries()]
    .map(([id, inner]) => fieldEvidenceFromMap(id, inner))
    .sort((a, b) => a.recordedAt - b.recordedAt || a.id.localeCompare(b.id));
}

export function recordFieldEvidence(
  doc: Y.Doc,
  input: RecordFieldEvidenceInput,
  options: WithNow = {},
): FieldEvidence {
  const now = timestamp(options);
  const id = fieldEvidenceId(input.entityType, input.entityId, input.field);
  const inner = getFieldEvidenceMap(doc).get(id) ?? new Y.Map<unknown>();
  doc.transact(() => {
    for (const [key, value] of Object.entries(input)) setOrDelete(inner, key, value);
    inner.set('recordedAt', now);
    if (!getFieldEvidenceMap(doc).has(id)) getFieldEvidenceMap(doc).set(id, inner);
  });
  return fieldEvidenceFromMap(id, inner);
}

export function listProgressEvents(doc: Y.Doc): ProgressEvent[] {
  return [...getProgressEventsMap(doc).entries()]
    .map(([id, inner]) => progressEventFromMap(id, inner))
    .sort((a, b) => b.occurredAt - a.occurredAt || a.id.localeCompare(b.id));
}

export function recordProgressEvent(
  doc: Y.Doc,
  input: RecordProgressEventInput,
  options: WithNow = {},
): ProgressEvent {
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  for (const [key, value] of Object.entries(input)) setOrDelete(inner, key, value);
  inner.set('occurredAt', input.occurredAt ?? now);
  inner.set('createdAt', now);
  getProgressEventsMap(doc).set(id, inner);
  return progressEventFromMap(id, inner);
}

export function listEnrichmentEvents(doc: Y.Doc): EnrichmentEvent[] {
  return [...getEnrichmentEventsMap(doc).entries()]
    .map(([id, inner]) => enrichmentEventFromMap(id, inner))
    .sort((a, b) => b.createdAt - a.createdAt || a.id.localeCompare(b.id));
}

export function recordEnrichmentEvent(
  doc: Y.Doc,
  input: RecordEnrichmentEventInput,
  options: WithNow = {},
): EnrichmentEvent {
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  for (const [key, value] of Object.entries(input)) setOrDelete(inner, key, value);
  inner.set(
    'changes',
    (input.changes ?? []).map((change) => ({ ...change })),
  );
  inner.set('createdAt', now);
  getEnrichmentEventsMap(doc).set(id, inner);
  return enrichmentEventFromMap(id, inner);
}

export function getTriageDisposition(
  doc: Y.Doc,
  entityType: PrioritizationEntityTypeT,
  entityId: string,
): TriageDisposition | undefined {
  const id = dispositionId(entityType, entityId);
  const inner = getTriageDispositionsMap(doc).get(id);
  return inner ? triageDispositionFromMap(id, inner) : undefined;
}

export function listTriageDispositions(doc: Y.Doc): TriageDisposition[] {
  return [...getTriageDispositionsMap(doc).entries()]
    .map(([id, inner]) => triageDispositionFromMap(id, inner))
    .sort((a, b) => b.updatedAt - a.updatedAt || a.id.localeCompare(b.id));
}

export function setTriageDisposition(
  doc: Y.Doc,
  input: SetTriageDispositionInput,
  options: WithNow = {},
): TriageDisposition {
  const now = timestamp(options);
  const id = dispositionId(input.entityType, input.entityId);
  const map = getTriageDispositionsMap(doc);
  const existing = map.get(id);
  const inner = existing ?? new Y.Map<unknown>();
  doc.transact(() => {
    if (!existing) map.set(id, inner);
    for (const [key, value] of Object.entries(input)) setOrDelete(inner, key, value);
    inner.set('reasonCodes', [...new Set(input.reasonCodes)]);
    if (!inner.has('createdAt')) inner.set('createdAt', now);
    inner.set('updatedAt', now);
  });
  return triageDispositionFromMap(id, inner);
}

export function clearTriageDisposition(
  doc: Y.Doc,
  entityType: PrioritizationEntityTypeT,
  entityId: string,
): void {
  getTriageDispositionsMap(doc).delete(dispositionId(entityType, entityId));
}

function validateProtectedBlock(
  doc: Y.Doc,
  input: {
    localDate: string;
    timezone: string;
    durationMinutes: number;
    startAt?: number | undefined;
    endAt?: number | undefined;
    objectiveId?: string | undefined;
    initiativeId?: string | undefined;
  },
): void {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.localDate)) throw new Error('invalid localDate');
  if (!input.timezone.trim()) throw new Error('timezone is required');
  if (!Number.isInteger(input.durationMinutes) || input.durationMinutes <= 0) {
    throw new Error('durationMinutes must be a positive integer');
  }
  if ((input.objectiveId === undefined) === (input.initiativeId === undefined)) {
    throw new Error('exactly one protected-block target is required');
  }
  if (input.objectiveId !== undefined && !getObjectivesMap(doc).has(input.objectiveId)) {
    throw new Error('protected-block objective not found');
  }
  if (input.initiativeId !== undefined && !getInitiativesMap(doc).has(input.initiativeId)) {
    throw new Error('protected-block initiative not found');
  }
  if (input.startAt !== undefined && input.endAt !== undefined && input.endAt <= input.startAt) {
    throw new Error('endAt must be after startAt');
  }
}

function setProtectedBlockFields(inner: Y.Map<unknown>, input: CreateProtectedBlockInput): void {
  for (const [key, value] of Object.entries(input)) setOrDelete(inner, key, value);
}

function setOrDelete(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value === undefined) inner.delete(key);
  else inner.set(key, Array.isArray(value) ? [...value] : value);
}

function dailyCapacityFromMap(id: string, inner: Y.Map<unknown>): DailyCapacity {
  return {
    id,
    localDate: String(inner.get('localDate') ?? ''),
    timezone: String(inner.get('timezone') ?? ''),
    availableMinutes: Number(inner.get('availableMinutes') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
}

function protectedBlockFromMap(id: string, inner: Y.Map<unknown>): ProtectedBlock {
  const value: ProtectedBlock = {
    id,
    localDate: String(inner.get('localDate') ?? ''),
    timezone: String(inner.get('timezone') ?? ''),
    durationMinutes: Number(inner.get('durationMinutes') ?? 0),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
  copyNumber(inner, value, 'startAt');
  copyNumber(inner, value, 'endAt');
  copyString(inner, value, 'objectiveId');
  copyString(inner, value, 'initiativeId');
  copyString(inner, value, 'note');
  return value;
}

function fieldEvidenceFromMap(id: string, inner: Y.Map<unknown>): FieldEvidence {
  const value: FieldEvidence = {
    id,
    entityType: inner.get('entityType') as FieldEvidence['entityType'],
    entityId: String(inner.get('entityId') ?? ''),
    field: String(inner.get('field') ?? ''),
    valueState: inner.get('valueState') as FieldEvidence['valueState'],
    source: inner.get('source') as FieldEvidence['source'],
    actor: String(inner.get('actor') ?? ''),
    recordedAt: Number(inner.get('recordedAt') ?? 0),
  };
  copyString(inner, value, 'tool');
  copyString(inner, value, 'valueHash');
  copyString(inner, value, 'confirmationId');
  copyString(inner, value, 'proposalFingerprint');
  return value;
}

function progressEventFromMap(id: string, inner: Y.Map<unknown>): ProgressEvent {
  const value: ProgressEvent = {
    id,
    entityType: inner.get('entityType') as ProgressEvent['entityType'],
    entityId: String(inner.get('entityId') ?? ''),
    occurredAt: Number(inner.get('occurredAt') ?? 0),
    source: inner.get('source') as ProgressEvent['source'],
    actor: String(inner.get('actor') ?? ''),
    createdAt: Number(inner.get('createdAt') ?? 0),
  };
  copyString(inner, value, 'note');
  copyString(inner, value, 'tool');
  return value;
}

function enrichmentEventFromMap(id: string, inner: Y.Map<unknown>): EnrichmentEvent {
  const rawChanges = inner.get('changes');
  const value: EnrichmentEvent = {
    id,
    entityType: inner.get('entityType') as EnrichmentEvent['entityType'],
    entityId: String(inner.get('entityId') ?? ''),
    action: inner.get('action') as EnrichmentEvent['action'],
    changes: Array.isArray(rawChanges)
      ? (rawChanges as EnrichmentChange[]).map((change) => ({ ...change }))
      : [],
    actor: String(inner.get('actor') ?? ''),
    createdAt: Number(inner.get('createdAt') ?? 0),
  };
  copyString(inner, value, 'tool');
  copyString(inner, value, 'confirmationId');
  copyString(inner, value, 'proposalFingerprint');
  return value;
}

function triageDispositionFromMap(id: string, inner: Y.Map<unknown>): TriageDisposition {
  const reasons = inner.get('reasonCodes');
  const value: TriageDisposition = {
    id,
    entityType: inner.get('entityType') as TriageDisposition['entityType'],
    entityId: String(inner.get('entityId') ?? ''),
    disposition: inner.get('disposition') as TriageDisposition['disposition'],
    reasonCodes: Array.isArray(reasons) ? [...(reasons as string[])] : [],
    sourceFingerprint: String(inner.get('sourceFingerprint') ?? ''),
    actor: String(inner.get('actor') ?? ''),
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
  copyNumber(inner, value, 'resurfaceAt');
  copyString(inner, value, 'tool');
  return value;
}

function copyString<T extends object>(inner: Y.Map<unknown>, target: T, key: keyof T): void {
  const value = inner.get(String(key));
  if (typeof value === 'string') (target as Record<string, unknown>)[String(key)] = value;
}

function copyNumber<T extends object>(inner: Y.Map<unknown>, target: T, key: keyof T): void {
  const value = inner.get(String(key));
  if (typeof value === 'number') (target as Record<string, unknown>)[String(key)] = value;
}
